import React from "react";
import styled, { css, keyframes } from "styled-components";
import Button from "./Button";
import { tokens } from "../../public/tokens";
import { MOBILE_BREAKPOINT } from "./breakpoints";

const pulse = keyframes({
    "0%, 100%": { opacity: 0.55 },
    "50%": { opacity: 1 },
});

const STRIP_PADDING = "13px";

const StyledSummaryStrip = styled.section({
    width: "100%",
    padding: STRIP_PADDING,
    background: tokens.colors.surfaces["container-background"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],
});

const StyledProgrammeCard = styled.div({
    width: "100%",
    minHeight: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: tokens.globals["spacing-md"],

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        alignItems: "flex-start",
        flexDirection: "column",
    },
});

/*
 * `flex: 0 1 auto` keeps this group at its own content width instead of growing to fill a
 * wide page; the action button aligns to the right via its own `margin-left: auto`.
 */
const StyledMain = styled.div({
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-md"],
    flex: "0 1 auto",

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        flexWrap: "wrap",
    },
});

const StyledVersionInformation = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
    flexShrink: 0,
});

const StyledVersionName = styled.span({
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["xl-18px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: "20px",
    whiteSpace: "nowrap",
});

const StyledDivider = styled.span({
    width: tokens.globals["spacing-px"],
    height: "35px",
    flex: `0 0 ${tokens.globals["spacing-px"]}`,
    background: tokens.colors.surfaces["border-divider"],

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        display: "none",
    },
});

const statusStyles = {
    active: {
        background: tokens.colors.states.success["100"],
        foreground: tokens.colors.states.success["500"],
    },
    inactive: {
        background: tokens.colors.neutral.grey.Alpha["10"],
        foreground: tokens.colors.text.secondary,
    },
    locked: {
        background: tokens.colors.states.warning["500"],
        foreground: tokens.colors.neutral.white,
    },
    unlocked: {
        background: tokens.colors.neutral.grey.Alpha["10"],
        foreground: tokens.colors.text.secondary,
    },
};

const StyledStatusBadge = styled.span(({ $variant = "active" }) => {
    const style = statusStyles[$variant] || statusStyles.active;

    return {
        minHeight: "22px",
        maxHeight: "22px",
        padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`,
        borderRadius: tokens.globals["border-radius"],
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: tokens.globals["spacing-1"],
        background: style.background,
        color: style.foreground,
        fontFamily: tokens.typography.fontFamilies["albert-sans"],
        fontSize: tokens.typography.fontSize["md-12px"],
        fontWeight: tokens.typography.fontWeights.semibold,
        lineHeight: 1,
        whiteSpace: "nowrap",

        i: {
            width: tokens.globals["spacing-2-5"],
            fontSize: tokens.typography.fontSize["sm-10px"],
            lineHeight: 1,
            textAlign: "center",
            flexShrink: 0,
        },
    };
});

const StyledQCInfo = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
    flexShrink: 0,
});

const StyledAvatar = styled.span(({ $hasImage = false }) => ({
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    background: $hasImage
        ? "transparent"
        : tokens.colors.neutral.grey["300"],
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",

    img: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
}));

const StyledInitials = styled.span({
    color: tokens.colors.neutral.black,
    fontSize: tokens.typography.fontSize["sm-10px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: 1,
});

const StyledInfo = styled.span({
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-0-5"],
});

const StyledName = styled.span({
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "12px",
});

const StyledRole = styled.span({
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["sm-10px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "12px",
    whiteSpace: "nowrap",
});

const StyledNotAssignedText = styled.span({
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "12px",
    whiteSpace: "nowrap",
});

const StyledAlertDot = styled.i({
    marginLeft: tokens.globals["spacing-1"],
    color: tokens.colors.ui.destructive,
    fontSize: "10px",
    lineHeight: 1,
});

const StyledNotAssignedIcon = styled.span({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "28px",
    height: "28px",
});

const StyledAssign = styled.button({
    width: "fit-content",
    padding: 0,
    border: 0,
    background: "transparent",
    color: tokens.colors.brand.blue["500"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["md-12px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "12px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-0-5"],

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },

    "&:disabled": {
        opacity: 0.6,
        cursor: "not-allowed",
    },
});

const StyledCounters = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-5"],
    flexShrink: 0,

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        flexWrap: "wrap",
    },
});

const StyledCounter = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    flexShrink: 0,
});

const StyledCounterLabel = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "12px",
    whiteSpace: "nowrap",

    i: {
        width: tokens.globals["spacing-3-5"],
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "14px",
        textAlign: "center",
        flexShrink: 0,
    },
});

const StyledCounterValueWrapper = styled.span({
    height: tokens.globals["spacing-5"],
    paddingLeft: tokens.globals["spacing-5"],
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
});

const StyledCounterValue = styled.span({
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "12px",
    whiteSpace: "nowrap",
});

const StyledSkeleton = styled.span({
    width: "33.21px",
    minWidth: "33.21px",
    height: tokens.globals["spacing-3"],
    display: "inline-block",
    borderRadius: tokens.globals["border-radius"],
    background: `linear-gradient(90deg, ${tokens.colors.neutral.grey["100"]} 0%, ${tokens.colors.neutral.grey["100"]} 45%, ${tokens.colors.neutral.grey["300"]} 100%)`,
    animation: css`${pulse} 1.2s ease-in-out infinite`,
});

/*
 * Extends the shared `Button` (theme-aware background) so this follows the AMI tenant's
 * applied theme; height/radius are pinned to the Figma spec rather than Button's generic sizes.
 */
const StyledOpenButton = styled(Button)({
    height: "35px",
    marginLeft: "auto",
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
    flexShrink: 0,
    whiteSpace: "nowrap",

    i: {
        width: tokens.globals["spacing-4"],
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "14px",
        textAlign: "center",
    },
});

function StatusBadge({ label, icon, iconClass = "fal", variant }) {
    return (
        <StyledStatusBadge $variant={variant}>
            <i className={`${iconClass} fa-${icon}`} aria-hidden="true" />
            {label}
        </StyledStatusBadge>
    );
}

function QualificationCoordinator({ coordinator, onAssign, assignDisabled = false, assignDisabledReason, isLoading = false }) {
    if (isLoading) {
        return (
            <StyledQCInfo aria-label="Loading Qualification Co-ordinator">
                <StyledAvatar>
                    <StyledInitials>...</StyledInitials>
                </StyledAvatar>

                <StyledInfo>
                    <StyledName>Loading...</StyledName>
                    <StyledRole>Qualification Co-ordinator</StyledRole>
                </StyledInfo>
            </StyledQCInfo>
        );
    }

    if (coordinator) {
        const coordinatorName = coordinator.name || "";
        const initials = coordinator.initials || coordinatorName.split(" ").filter(Boolean).map((part) => part[0]).join("") || "??";

        return (
            <StyledQCInfo>
                <StyledAvatar $hasImage={Boolean(coordinator.avatar)}>
                    {coordinator.avatar ? (
                        <img src={coordinator.avatar} alt="" />
                    ) : (
                        <StyledInitials>{initials.slice(0, 2).toUpperCase()}</StyledInitials>
                    )}
                </StyledAvatar>

                <StyledInfo>
                    <StyledName>{coordinatorName}</StyledName>
                    <StyledRole>Qualification Co-ordinator</StyledRole>
                </StyledInfo>
            </StyledQCInfo>
        );
    }

    return (
        <StyledQCInfo>
            <StyledNotAssignedIcon aria-hidden="true">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="14" cy="14" r="14" fill="#E39219" fillOpacity="0.1" />
                    <path d="M12.3516 12.7328C13.5135 12.7328 14.4641 11.7822 14.4641 10.6203C14.4641 9.45841 13.5135 8.50778 12.3516 8.50778C11.1897 8.50778 10.2391 9.45841 10.2391 10.6203C10.2391 11.7822 11.1897 12.7328 12.3516 12.7328ZM12.3516 7.24028C14.2264 7.24028 15.7316 8.74544 15.7316 10.6203C15.7316 12.4951 14.2264 14.0003 12.3516 14.0003C10.4767 14.0003 8.97156 12.4951 8.97156 10.6203C8.97156 8.74544 10.4767 7.24028 12.3516 7.24028ZM11.0841 16.5353C9.20921 16.5353 7.70405 18.0405 7.70405 19.9153V20.1266C7.70405 20.4699 7.41358 20.7603 7.0703 20.7603C6.72701 20.7603 6.43654 20.4699 6.43654 20.1266V19.9153C6.43654 17.3539 8.52265 15.2678 11.0841 15.2678H13.6191C16.1805 15.2678 18.2666 17.3539 18.2666 19.9153V20.1266C18.2666 20.4699 17.9761 20.7603 17.6329 20.7603C17.2896 20.7603 16.9991 20.4699 16.9991 20.1266V19.9153C16.9991 18.0405 15.4939 16.5353 13.6191 16.5353H11.0841ZM19.5341 10.409C19.1116 10.409 18.7683 10.7523 18.7683 11.1748C18.7683 11.5445 18.4779 11.8086 18.1346 11.8086C17.7913 11.8086 17.5008 11.5445 17.5008 11.1748C17.5008 10.0658 18.3986 9.14154 19.5341 9.14154C20.6696 9.14154 21.5674 10.0658 21.5674 11.1748C21.5674 12.3895 20.6696 12.8912 20.1679 13.0761C20.0886 13.3666 19.851 13.5778 19.5341 13.5778C19.1908 13.5778 18.9004 13.2873 18.9004 12.9441C18.9004 12.4159 19.2965 12.0198 19.6926 11.9142C19.851 11.8614 20.0094 11.7558 20.1151 11.6501C20.2207 11.5445 20.2999 11.4125 20.2999 11.1748C20.2999 10.7523 19.9566 10.409 19.5341 10.409ZM18.7947 14.8453C18.7947 14.4492 19.138 14.1059 19.5341 14.1059C19.9302 14.1059 20.2735 14.4492 20.2735 14.8453C20.2735 15.2414 19.9302 15.5847 19.5341 15.5847C19.138 15.5847 18.7947 15.2414 18.7947 14.8453Z" fill={tokens.colors.states.warning["500"]} />
                </svg>
            </StyledNotAssignedIcon>

            <StyledInfo>
                <StyledNotAssignedText>
                    Not Assigned
                    <StyledAlertDot
                        className="fas fa-exclamation-circle"
                        aria-hidden="true"
                    />
                </StyledNotAssignedText>

                {onAssign && (
                    <StyledAssign
                        type="button"
                        onClick={onAssign}
                        disabled={assignDisabled}
                        title={assignDisabled ? assignDisabledReason : undefined}
                    >
                        Assign

                        <svg width="10" height="8" viewBox="0 0 9.99512 7.49023" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M9.86328 4.07715L6.58203 7.3584C6.40625 7.53418 6.11328 7.53418 5.91797 7.3584C5.74219 7.18262 5.74219 6.87012 5.91797 6.69434L8.39844 4.21387H0.46875C0.214844 4.21387 0 3.99902 0 3.74512C0 3.49121 0.214844 3.27637 0.46875 3.27637H8.39844L5.91797 0.795898C5.74219 0.620117 5.74219 0.307617 5.91797 0.131836C6.09375 -0.0439453 6.40625 -0.0439453 6.58203 0.131836L9.86328 3.41309C10.0391 3.58887 10.0391 3.90137 9.86328 4.07715Z" fill={tokens.colors.brand.blue["500"]} />
                        </svg>
                    </StyledAssign>
                )}
            </StyledInfo>
        </StyledQCInfo>
    );
}

function SummaryCounter({ icon, label, value, isLoading }) {
    return (
        <StyledCounter>
            <StyledCounterLabel>
                <i className={`fal fa-${icon}`} aria-hidden="true" />
                {label}
            </StyledCounterLabel>

            <StyledCounterValueWrapper>
                {isLoading ? (
                    <StyledSkeleton aria-label={`Loading ${label}`} />
                ) : (
                    <StyledCounterValue>{value ?? 0}</StyledCounterValue>
                )}
            </StyledCounterValueWrapper>
        </StyledCounter>
    );
}

/**
 * PMC "Version Summary Strip": version name, Active/Locked badges, the read-only Qualification Co-ordinator, and summary counters.
 * Presentational — state resolves via the caller's `resolveVersionSummaryState`.
 * @param {boolean} [props.isResolving] `true` shows a placeholder before the caller knows if a version exists.
 * @param {boolean} [props.hasVersion] `false` collapses the strip to a "Create a Version"-style prompt; other props are ignored.
 * @param {() => void} [props.onCreateVersion] Called from the no-version state's button; omit to hide it.
 * @param {{ name, avatar?, initials? }} [props.coordinator] `null`/`undefined` shows "Not Assigned".
 * @param {Object} [props.totals] `{ programmes, modules, students, graduates, lockedProgrammes }`.
 * @param {() => void} [props.onAction] Right-hand action handler; defaults to `onOpen`.
 */
export function ProgrammeSummaryStrip({
    isResolving = false,
    hasVersion = true,
    noVersionMessage = "Create a Version",
    onCreateVersion,
    createVersionDisabled = false,
    createVersionDisabledReason,
    versionName = "—",
    isActive = false,
    isLocked = false,
    coordinator,
    onAssign,
    assignDisabled = false,
    assignDisabledReason,
    totals = {},
    isLoading = false,
    isCoordinatorLoading = false,
    onOpen,
    openDisabled = false,
    openDisabledReason,
    onAction = onOpen,
    actionLabel = "Open",
    actionIcon = "folder-open",
    actionDisabled = openDisabled,
    actionDisabledReason = openDisabledReason,
    className,
}) {
    if (isResolving) {
        return (
            <StyledSummaryStrip className={className} aria-label="Programme summary">
                <StyledProgrammeCard>
                    <StyledSkeleton style={{ width: "160px" }} aria-label="Loading programme summary" />
                </StyledProgrammeCard>
            </StyledSummaryStrip>
        );
    }

    if (!hasVersion) {
        return (
            <StyledSummaryStrip className={className} aria-label="Programme summary">
                <StyledProgrammeCard>
                    <StyledVersionName>{noVersionMessage}</StyledVersionName>

                    {onCreateVersion && (
                        <StyledOpenButton
                            type="button"
                            variant="primary"
                            size="lg"
                            onClick={onCreateVersion}
                            disabled={createVersionDisabled}
                            title={createVersionDisabled ? createVersionDisabledReason : undefined}
                        >
                            <i className="fal fa-plus" aria-hidden="true" />
                            Create version
                        </StyledOpenButton>
                    )}
                </StyledProgrammeCard>
            </StyledSummaryStrip>
        );
    }

    const counters = [
        { key: "programmes", icon: "books", label: "Programmes" },
        { key: "modules", icon: "book", label: "Modules" },
        { key: "students", icon: "users", label: "Students" },
        { key: "graduates", icon: "graduation-cap", label: "Graduates" },
        { key: "lockedProgrammes", icon: "lock", label: "Locked Programmes" },
    ];

    return (
        <StyledSummaryStrip className={className} aria-label="Programme summary">
            <StyledProgrammeCard>
                <StyledMain>
                    <StyledVersionInformation>
                        <StyledVersionName>{versionName}</StyledVersionName>

                        <StatusBadge
                            label={isActive ? "Active" : "Inactive"}
                            icon={isActive ? "circle-check" : "times-circle"}
                            iconClass="fas"
                            variant={isActive ? "active" : "inactive"}
                        />

                        <StatusBadge
                            label={isLocked ? "Locked" : "Unlocked"}
                            icon={isLocked ? "lock" : "unlock"}
                            variant={isLocked ? "locked" : "unlocked"}
                        />
                    </StyledVersionInformation>

                    <StyledDivider aria-hidden="true" />

                    <QualificationCoordinator
                        coordinator={coordinator}
                        onAssign={onAssign}
                        assignDisabled={assignDisabled}
                        assignDisabledReason={assignDisabledReason}
                        isLoading={isCoordinatorLoading}
                    />

                    <StyledDivider aria-hidden="true" />

                    <StyledCounters>
                        {counters.map((counter) => (
                            <SummaryCounter
                                key={counter.key}
                                icon={counter.icon}
                                label={counter.label}
                                value={totals[counter.key]}
                                isLoading={isLoading}
                            />
                        ))}
                    </StyledCounters>
                </StyledMain>

                {onAction && (
                    <StyledOpenButton
                        type="button"
                        variant="primary"
                        size="lg"
                        onClick={onAction}
                        disabled={actionDisabled}
                        title={actionDisabled ? actionDisabledReason : undefined}
                    >
                        <i className={`fal fa-${actionIcon}`} aria-hidden="true" />
                        {actionLabel}
                    </StyledOpenButton>
                )}
            </StyledProgrammeCard>
        </StyledSummaryStrip>
    );
}

ProgrammeSummaryStrip.displayName = "ProgrammeSummaryStrip";

export default ProgrammeSummaryStrip;
